import {
  ComponentProps,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { FormProvider } from 'react-hook-form';
import { OkButton } from 'src/components/Buttons/OkButton';
import {
  isCampaignActive,
  useSponsoredListingCampaignForm,
} from 'src/components/SponsoredListings';
import { UpdateCampaignStateButton } from 'src/components/SponsoredListings/UpdateCampaignStateButton';
import {
  AdCampaignsDataContextProvider,
  useAdCampaignsDataContext,
} from 'src/contexts/AdCampaignsDataContext';
import { useContent, useFormattedContent } from 'src/contexts/ContentContext';
import { EventMetricsProvider } from 'src/contexts/EventMetricsContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Stack } from 'src/core/ui';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { FLIGHT_DURATION_TO_CID } from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { hasChanged, posFieldFromValues } from 'src/utils/posFieldUtils';
import {
  AdCampaignState,
  BiddingMode,
  Event,
  Feature,
  FlightDuration,
  ListingMetrics,
  Permission,
  SponsoredListingsCampaign,
  SponsoredListingsCampaignInput,
} from 'src/WebApiController';

import { CancellableFormFooter } from '../../common';
import { CancellableFormHeader } from '../../common/CancellableFormHeader';
import { modalDetails } from '../../common/Modals.css';
import { ModalProps } from '../../Modal';
import { ModalBody, ModalFooter } from '../../Modal/Modal.styled';
import { MINIMUM_BASE_BID } from '../sponsoredListings.util';
import * as styles from './CampaignModal.css';
import { CampaignModalBody } from './CampaignModalBody';
import { CampaignModalBodyV2 } from './CampaignModalBodyV2';
import { CampaignModalFooter } from './CampaignModalFooter';
import { CampaignModalHeader } from './CampaignModalHeader';
import { CampaignModalIntroBody } from './CampaignModalIntroBody';
import { CampaignModalIntroHeader } from './CampaignModalIntroHeader';

type CampaignModalProps = {
  campaign?: SponsoredListingsCampaign | null;
  eventId: number;
  cancelTo?: ModalProps;
};

export const CampaignModal = (props: CampaignModalProps) => {
  return (
    <AdCampaignsDataContextProvider loadSalesDetails>
      <CampaignModalForm {...props} />
    </AdCampaignsDataContextProvider>
  );
};

const CampaignModalForm = ({
  campaign,
  eventId,
  cancelTo,
}: CampaignModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { refreshData } = useAdCampaignsDataContext();
  const { closeModal } = useContext(ModalContext);

  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );
  const hasAdPlatformCreatePermission =
    useUserHasAnyOfPermissions(Permission.AdPlatform_CampaignsCreate) &&
    hasAdPlatformViewPermission;

  const { methods, onSubmit, event, eventNameDisplay, metrics } =
    useSponsoredListingCampaignForm({
      eventId: eventId,
      campaign,
      onBeforeSubmit: () => setIsLoading(true),
      onAfterSubmit: (success) => {
        setIsLoading(false);
        if (success) {
          closeModal(true);
          refreshData();
        }
      },
      onError: () => setIsLoading(false),
    });

  const canOpenModal = useMemo(() => {
    if (!event) return false;

    // Existing campaign requires the view permission
    if (campaign && !hasAdPlatformViewPermission) {
      return false;
    }

    // no campaign requires the create permission
    return hasAdPlatformCreatePermission;
  }, [
    campaign,
    event,
    hasAdPlatformCreatePermission,
    hasAdPlatformViewPermission,
  ]);

  if (!canOpenModal) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <CampaignModalContent
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        eventId={eventId}
        event={event!.event}
        campaign={campaign}
        cancelTo={cancelTo}
        eventNameDisplay={eventNameDisplay}
        metrics={metrics}
        onSubmit={onSubmit}
        {...methods}
      />
    </FormProvider>
  );
};

const CampaignModalContent = ({
  event,
  campaign,
  metrics,
  eventNameDisplay,
  cancelTo,
  formState,
  handleSubmit,
  watch,
  getValues,
  clearErrors,
  setError,
  setValue,
  setFocus,
  isLoading,
  setIsLoading,
  onSubmit,
}: Omit<
  ComponentProps<typeof FormProvider<SponsoredListingsCampaignInput, unknown>>,
  'children'
> &
  CampaignModalProps & {
    event: Event;
    eventNameDisplay: string;
    metrics?: ListingMetrics;
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;
    onSubmit: (formValue: SponsoredListingsCampaignInput) => void;
  }) => {
  const flightDuration = watch('flightDuration');
  const { refreshData } = useAdCampaignsDataContext();
  const { closeModal } = useContext(ModalContext);
  const hasCpcUi = useUserHasFeature(Feature.SponsoredListingsV2_CPC_Input);

  const requiredText = useContent(ContentId.Required);
  const currentFlightBudgetLessThanSpendText = useContent(
    ContentId.CurrentFlightBudgetLessThanSpendError
  );
  const flightDurationText = useContent(
    FLIGHT_DURATION_TO_CID[flightDuration.value ?? FlightDuration.Daily]
  );
  const baseBidExceedsBudgetText = useFormattedContent(
    FormatContentId.BaseBidExceedsBudgetError,
    [`${flightDurationText}`]
  );

  const validateForm = useCallback(
    (
      formValues: SponsoredListingsCampaignInput
    ): {
      isValid: boolean;
      firstErrorField: keyof SponsoredListingsCampaignInput | null;
    } => {
      let isValid = true;
      let firstErrorField: keyof SponsoredListingsCampaignInput | null = null;

      // Clear all previous errors
      clearErrors();

      // Validate campaign name
      if (
        !formValues.campaignName.value ||
        formValues.campaignName.value.trim().length === 0
      ) {
        setError('campaignName', {
          type: 'required',
          message: requiredText,
        });
        if (!firstErrorField) firstErrorField = 'campaignName';
        isValid = false;
      }

      // Validate budget
      if (
        !formValues.recurringFlightBudget.value ||
        formValues.recurringFlightBudget.value <= 0
      ) {
        setError('recurringFlightBudget', {
          type: 'required',
          message: requiredText,
        });
        if (!firstErrorField) firstErrorField = 'recurringFlightBudget';
        isValid = false;
      }

      // Validate if manual bidding is selected
      if (formValues.biddingMode.value === BiddingMode.Manual) {
        if (formValues.baseBid.value == null) {
          setError('baseBid', {
            type: 'required',
            message: requiredText,
          });
          if (!firstErrorField) firstErrorField = 'baseBid';
          isValid = false;
        } else if (formValues.baseBid.value < MINIMUM_BASE_BID) {
          // Auto-correct to minimum instead of showing error
          setValue(
            'baseBid',
            posFieldFromValues(MINIMUM_BASE_BID, campaign?.baseBid)
          );
          formValues.baseBid.value = MINIMUM_BASE_BID;
        }
      }

      // Validate that the base bid cannot be more than the recurring flight budget
      if (
        formValues.biddingMode.value === BiddingMode.Manual &&
        formValues.baseBid.value &&
        // Base bid cannot exceed either the recurring budget or current flight budget (if editing active flight)
        (formValues.baseBid.value >
          (formValues.recurringFlightBudget.value ?? 0) ||
          (formValues.currentFlightBudget.value &&
            formValues.baseBid.value > formValues.currentFlightBudget.value))
      ) {
        setError('baseBid', {
          type: 'max',
          message: baseBidExceedsBudgetText as string,
        });
        if (!firstErrorField) firstErrorField = 'baseBid';
        isValid = false;
      }

      // Validate the current flight budget has to be greater than spend
      if (
        campaign?.activeFlight &&
        (formValues.currentFlightBudget.value || 0) <
          (campaign.activeFlight.flightSpend || 0)
      ) {
        setError('currentFlightBudget', {
          type: 'min',
          message: currentFlightBudgetLessThanSpendText,
        });
        if (!firstErrorField) firstErrorField = 'currentFlightBudget';
        isValid = false;
      }

      return {
        isValid,
        firstErrorField,
      };
    },
    [
      clearErrors,
      campaign?.activeFlight,
      setError,
      setValue,
      requiredText,
      baseBidExceedsBudgetText,
      currentFlightBudgetLessThanSpendText,
    ]
  );

  const hasChanges = useCallback(
    (formValue: SponsoredListingsCampaignInput) => {
      if (formState.defaultValues) {
        if (formValue.campaignId < 0) {
          // New campaign - all fields are changed
          return true;
        }

        return (
          hasChanged(
            formValue.campaignName,
            formState.defaultValues.campaignName
          ) ||
          hasChanged(
            formValue.flightDuration,
            formState.defaultValues.flightDuration
          ) ||
          hasChanged(
            formValue.recurringFlightBudget,
            formState.defaultValues.recurringFlightBudget
          ) ||
          hasChanged(
            formValue.targetTicketClassIds,
            formState.defaultValues.targetTicketClassIds
          ) ||
          hasChanged(
            formValue.biddingMode,
            formState.defaultValues.biddingMode
          ) ||
          hasChanged(formValue.baseBid, formState.defaultValues.baseBid) ||
          hasChanged(
            formValue.currentFlightBudget,
            formState.defaultValues.currentFlightBudget
          )
        );
      }
      return false;
    },
    [formState.defaultValues]
  );

  const formHasChanges = hasChanges(watch());

  const { hasCampaignsQuery } = useAdCampaignsDataContext();
  const [viewedIntroPage, setViewedIntroPage] = useState(false);

  const showIntroPage = useMemo(
    () => !campaign && !hasCampaignsQuery.data && !viewedIntroPage,
    [campaign, hasCampaignsQuery.data, viewedIntroPage]
  );

  const onSubmitHandler = useCallback(() => {
    if (showIntroPage) {
      setViewedIntroPage(true);
      return;
    }

    const formValues = getValues();
    const { isValid, firstErrorField } = validateForm(formValues);

    if (!isValid && firstErrorField && setFocus) {
      setFocus(firstErrorField);
      return;
    }

    if (isValid) {
      handleSubmit(onSubmit)();
    }
  }, [
    showIntroPage,
    getValues,
    validateForm,
    handleSubmit,
    onSubmit,
    setFocus,
  ]);

  const BodyComponent = useMemo(
    () => (hasCpcUi ? CampaignModalBodyV2 : CampaignModalBody),
    [hasCpcUi]
  );

  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );
  const hasAdPlatformCreatePermission =
    useUserHasAnyOfPermissions(Permission.AdPlatform_CampaignsCreate) &&
    hasAdPlatformViewPermission;
  const hasAdPlatformEditPermission =
    useUserHasAnyOfPermissions(
      Permission.AdPlatform_CampaignsEdit,
      Permission.AdPlatform_Edit
    ) && hasAdPlatformViewPermission;

  const isDisabled = isLoading || formState.isSubmitting;

  const canCreateCampaign =
    !isDisabled && !campaign && hasAdPlatformCreatePermission;
  const canEditCampaign =
    !isDisabled && campaign && hasAdPlatformEditPermission;

  return (
    <EventMetricsProvider event={event}>
      <CancellableFormHeader
        className={styles.modalHeader}
        cancelTo={cancelTo}
        disabled={isDisabled}
        showDialogOnCancel={formHasChanges}
      >
        {!showIntroPage ? (
          <CampaignModalHeader
            event={event}
            eventNameDisplay={eventNameDisplay}
            metrics={metrics}
          />
        ) : (
          <CampaignModalIntroHeader />
        )}
      </CancellableFormHeader>

      {hasCampaignsQuery.isLoading ? (
        <PosSpinner />
      ) : (
        <ModalBody>
          <div className={modalDetails}>
            {!showIntroPage ? (
              <BodyComponent
                campaign={campaign}
                disabled={isDisabled}
                eventNameDisplay={eventNameDisplay}
                event={event}
                metrics={metrics}
              />
            ) : (
              <CampaignModalIntroBody />
            )}
          </div>
        </ModalBody>
      )}

      <ModalFooter>
        <Stack direction="column" width="full" gap="l">
          {!showIntroPage && <CampaignModalFooter />}
          <CancellableFormFooter
            cancelTo={cancelTo}
            disabled={isDisabled}
            showDialogOnCancel={formHasChanges}
            hideCloseButton
          >
            <Stack justifyContent="spaceBetween">
              {campaign && (
                <UpdateCampaignStateButton
                  className={styles.boostButtonCommon}
                  campaign={campaign}
                  state={
                    isCampaignActive(campaign)
                      ? AdCampaignState.Paused
                      : AdCampaignState.Active
                  }
                  disabled={!canEditCampaign}
                  onBeforePause={() => setIsLoading(true)}
                  onAfterPause={async (success) => {
                    if (success) {
                      await refreshData?.();
                      setIsLoading(false);
                      closeModal(true);
                    }
                  }}
                />
              )}
              <OkButton
                className={styles.boostButtonCommon}
                style={{ width: campaign ? 'auto' : '100%' }}
                variant="regular"
                shape="rect"
                onClick={onSubmitHandler}
                disabled={
                  !formHasChanges ||
                  (!showIntroPage
                    ? !(canCreateCampaign || canEditCampaign)
                    : false)
                }
                textContentId={
                  campaign
                    ? ContentId.Update
                    : hasCampaignsQuery.data || viewedIntroPage
                      ? ContentId.BoostEvent
                      : ContentId.GetStarted
                }
              />
            </Stack>
          </CancellableFormFooter>
        </Stack>
      </ModalFooter>
    </EventMetricsProvider>
  );
};
